import React, { createContext, useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'
import numeral from 'numeral'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  TextField,
  Grid2 as Grid,
  Stack,
  Typography,
  Divider,
  Tooltip,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Popover,
  useTheme,
  CircularProgress,
} from '@mui/material'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer'
import '@cyntler/react-doc-viewer/dist/index.css'

import FilePickerDialog from '../../../../../screens/Readiness/FilePickerDialog'

const { REACT_APP_USER_CLIENT } = process.env

const ReadinessContext = createContext(null)

const MIN_SCORE = 0
const MAX_SCORE = 10

const DETAILS_MAP: Record<string, { label: string; inputSuffix?: string }> = {
  dscRatio: { label: 'Debt Service Coverage Ratio' },
  irr: { label: 'IRR(%)' },
  lcoe: { label: 'LCOE ($/MWh)' },
  ppaPrice: { label: 'PPA Price ($/MWh)' },
  iso: { label: 'Delivery Hub' },
  wgtForAvgPrice: { label: 'Weighted Avg Forward Price ($/MWh)' },
  wgtAvgLcoeSpread: {
    label: 'Weighted Avg Fwd Price - LCOE spread (%)',
    inputSuffix: '%',
  },
  ppaLcoeSpread: { label: 'PPA Price - LCOE Spread (%)', inputSuffix: '%' },
  wgtAvgPpaSpread: {
    label: 'Weighted Avg Forward Price - PPA Spread (%)',
    inputSuffix: '%',
  },
  powerProfitability: {
    label: 'Power Profitability Score (%)',
    inputSuffix: '%',
  },
  solarCapacity: { label: 'Solar Capacity (MW AC)' },
  bessCapacity: { label: 'BESS Capacity (MWh)' },
  ppaPriceBessAssumption: { label: 'PPA Price assumes payment for BESS?' },
  interconnectMaxPower: { label: 'Interconnect AC Max Power (MW)' },
  energyCommunity: { label: 'Energy Community?' },
  otherFedItcBonus: { label: 'Other Federal ITC bonus?' },
  federalITC: { label: 'Federal ITC (%)', inputSuffix: '%' },
  loanFraction: { label: 'Loan Fraction (%)', inputSuffix: '%' },
  itcAndLoanFraction: { label: 'ITC + Loan Fraction (%)', inputSuffix: '%' },
  loanInterestRate: { label: 'Loan Interest Rate (%)' },
  interconnectAndGenTieCosts: {
    label: 'Interconnect & Gen Tie Costs ($/kW AC)',
  },
  otherDevCost: {
    label: 'Other Development Costs ($/kW AC)',
  },
}

const GET_SYNCED_SCORE = gql`
  query GET_SYNCED_SCORE(
    $dscRatio: Float!
    $site: ID!
    $otherFedItcBonus: Boolean!
  ) {
    syncedScore: getSyncedScore(
      dscRatio: $dscRatio
      site: $site
      otherFedItcBonus: $otherFedItcBonus
    )
  }
`

const LINKED_FILE_FIELDS = gql`
  fragment LinkedFileFields on LinkedFile {
    dataroom
    dataroomName
    stage
    stageName
    file
    filename
    url
    mimetype
  }
`

const CORE_SCORE_FIELDS = gql`
  ${LINKED_FILE_FIELDS}
  fragment CoreScoreFields on Score {
    id
    site
    value
    published
    subscores {
      analyze {
        value
        description
        meta
        linkedFiles {
          ...LinkedFileFields
        }
      }
      interconnect {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
      landowner {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
      permitting {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
      offtake {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
      taxCredits {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
      buildability {
        value
        description
        linkedFiles {
          ...LinkedFileFields
        }
      }
    }
  }
`

const CREATE_SCORE = gql`
  ${CORE_SCORE_FIELDS}
  mutation CREATE_SCORE($score: ScoreInput!) {
    createScore(score: $score) {
      ...CoreScoreFields
    }
  }
`

const UPDATE_SCORE = gql`
  ${CORE_SCORE_FIELDS}
  mutation UPDATE_SCORE($id: ID!, $score: ScoreInput!) {
    updateScore(id: $id, score: $score) {
      ...CoreScoreFields
    }
  }
`

const FIND_SCORES = gql`
  ${CORE_SCORE_FIELDS}
  query FIND_SCORES($filter: JSON!) {
    scores: findScores(filter: $filter) {
      ...CoreScoreFields
    }
  }
`

const scoreSchema = (site: any) =>
  Yup.object({
    analyze: Yup.object().when('project', {
      is: () => !site.project,
      then: () => Yup.object(),
      otherwise: () =>
        Yup.object({
          value: Yup.number()
            .required('This is required! Enter 5 if unsure')
            .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
            .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
            .typeError('This is required! Enter 5 if unsure'),
          description: Yup.string().trim().nullable(),
          meta: Yup.object(),
        }),
    }),
    interconnect: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    landowner: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure.')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    permitting: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure.')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    offtake: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure.')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    taxCredits: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure.')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    buildability: Yup.object({
      value: Yup.number()
        .required('This is required! Enter 5 if unsure.')
        .min(MIN_SCORE, `Should be greater than or equals ${MIN_SCORE}`)
        .max(MAX_SCORE, `Should be less than or equals ${MAX_SCORE}`)
        .typeError('This is required! Enter 5 if unsure'),
      description: Yup.string().trim().nullable(),
    }),
    published: Yup.boolean(),
  })

const syncScoreSchema = Yup.object({
  dscRatio: Yup.number()
    .required('Enter a valid value!')
    .min(0, `Should be greater than or equals 0`)
    .max(3, `Should be less than or equals 3`)
    .typeError('Enter a valid value!'),
  otherFedItcBonus: Yup.boolean(),
})

const SUBSCORES = [
  { key: 'interconnect', label: 'Interconnect' },
  { key: 'landowner', label: 'Landowner Participation' },
  { key: 'permitting', label: 'Permitting' },
  { key: 'offtake', label: 'Offtake' },
  { key: 'taxCredits', label: 'Tax Credits' },
  { key: 'buildability', label: 'Buildability' },
]

const PREVIEWABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'image/webp',
  'text/plain',
  'text/csv',
  'text/html',
])

const isPreviewable = (mimetype: string) =>
  PREVIEWABLE_TYPES.has(mimetype) ||
  mimetype.startsWith('image/') ||
  mimetype.includes('word')

function LinkedFileRow({
  file,
  onUnlink,
}: {
  file: any
  onUnlink: (fileId: string) => void
}) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const canPreview = isPreviewable(file.mimetype || '')

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Link
        component="button"
        type="button"
        onClick={() =>
          canPreview ? setPreviewOpen(true) : window.open(file.url, '_blank')
        }
        sx={{
          fontSize: 12,
          color: theme.palette.textColor.main,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        {file.filename}
      </Link>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography
              sx={{ fontSize: 14, color: theme.palette.textColor.main }}
            >
              {file.filename}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <IconButton
                size="small"
                component="a"
                href={file.url}
                download
                rel="noopener noreferrer"
              >
                <CloudDownloadIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setPreviewOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DocViewer
            prefetchMethod="GET"
            pluginRenderers={DocViewerRenderers}
            documents={[
              {
                uri: file.url,
                fileType: file.mimetype || '',
                fileName: file.filename || '',
              },
            ]}
            style={{ minHeight: 600, maxHeight: 600, overflow: 'auto' }}
            config={{
              header: { disableHeader: true },
              noRenderer: {
                overrideComponent: () => (
                  <Stack
                    spacing={1}
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: 400,
                    }}
                  >
                    <Typography color="text.secondary">
                      No preview available
                    </Typography>
                  </Stack>
                ),
              },
            }}
          />
        </DialogContent>
      </Dialog>
      <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget)}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <Typography variant="caption">Unlink this file?</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setAnchorEl(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => {
                setAnchorEl(null)
                onUnlink(file.file)
              }}
            >
              Unlink
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  )
}

function LinkedFilesList({
  files = [],
  onUnlink,
}: {
  files?: any[]
  onUnlink: (fileId: string) => void
}) {
  if (files.length === 0) return null

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {files.map((file: any) => (
        <LinkedFileRow key={file.file} file={file} onUnlink={onUnlink} />
      ))}
    </Stack>
  )
}

function SyncDetails({ data, disabled }: { data: any; disabled: boolean }) {
  const { site } = useContext(ReadinessContext) as any
  const theme = useTheme()
  const [show, setShow] = useState(false)

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled}
        onClick={() => setShow(true)}
        sx={{ ml: 1 }}
      >
        Details
      </Button>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: 16,
                fontWeight: 'bold',
                color: theme.palette.textColor.main,
              }}
            >
              Analyze Subscore - {numeral(data?.score).format('0.[00]')} |{' '}
              {site.name}
            </Typography>
            <IconButton size="small" onClick={() => setShow(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small" sx={{ fontSize: 12 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Input</TableCell>
                  <TableCell>Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(DETAILS_MAP).map((key, idx) => (
                  <TableRow
                    key={key}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'action.hover' : 'transparent',
                    }}
                  >
                    <TableCell sx={{ fontSize: 12 }}>
                      {DETAILS_MAP[key].label}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {String(
                        typeof data?.[key]?.input === 'number'
                          ? numeral(data?.[key]?.input).format(
                              `0,0.[00]${DETAILS_MAP[key].inputSuffix || ''}`
                            )
                          : data?.[key]?.input === null
                          ? ''
                          : data?.[key]?.input
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {String(
                        typeof data?.[key]?.value === 'number'
                          ? numeral(data?.[key]?.value).format('0,0.[0]')
                          : data?.[key]?.value === null
                          ? ''
                          : data?.[key]?.value
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {data.outputCreatedAt && (
                  <TableRow>
                    <TableCell sx={{ fontSize: 12 }}>
                      Date of View Analyze Report
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {data.outputCreatedAt.input}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {new Date(data.outputCreatedAt.value).toLocaleString(
                        'en-US',
                        {
                          timeStyle: 'short',
                          dateStyle: 'short',
                        }
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SyncAnalyzeScoreForm({
  onSync,
  values,
}: {
  onSync: (syncedScore: any) => void
  values: any
}) {
  const { site } = useContext(ReadinessContext) as any
  const theme = useTheme()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(syncScoreSchema),
    defaultValues: {
      dscRatio: values?.dscRatio?.input,
      otherFedItcBonus: !!values?.otherFedItcBonus?.input,
    },
  })

  const [getSyncedScore, { loading, data: res }] =
    useLazyQuery(GET_SYNCED_SCORE)

  const onSubmit = (formValues: any) => {
    getSyncedScore({
      variables: {
        dscRatio: formValues.dscRatio,
        site: site.id,
        otherFedItcBonus: formValues.otherFedItcBonus,
      },
      onCompleted: (data: any) => {
        onSync(data.syncedScore)
      },
    })
  }

  return (
    <Box component="form" noValidate>
      <Divider />
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 'bold',
            color: theme.palette.textColor.main,
            mr: 1,
          }}
        >
          View Analyze Score Sync
        </Typography>
        <Tooltip
          placement="bottom"
          enterDelay={250}
          leaveDelay={400}
          title='Once the two inputs here are ready, click "Sync" to pull the data from the linked View Analyze report, and compute a subscore which will appear in the "View Analyze" box. Then you can click "Details" to see the data and calculations behind this subscore.'
        >
          <InfoOutlined
            sx={{
              fontSize: 12,
              color: theme.palette.primary.light,
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography
          sx={{
            fontSize: 12,
            color: theme.palette.textColor.main,
            mr: 1,
          }}
        >
          Debt Service Coverage Ratio*
        </Typography>
        <Tooltip
          placement="bottom"
          enterDelay={250}
          leaveDelay={400}
          title={
            <>
              From the spreadsheet <code>hst_financial_model.xlsx </code>{' '}
              produced by the relevant View Analyze report, find the first
              year's value for the "Debt Service Coverage Ratio" near the bottom
              of the sheet, and enter it here. It will usually be between 1.0
              and 2.0.
            </>
          }
        >
          <InfoOutlined
            sx={{
              fontSize: 12,
              color: theme.palette.primary.light,
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'baseline' }}>
        <TextField
          {...register('dscRatio')}
          type="number"
          size="small"
          fullWidth
          sx={{ '& input': { fontSize: 12 }, bgcolor: 'white' }}
          error={!!errors.dscRatio}
          helperText={errors.dscRatio?.message}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={handleSubmit(onSubmit)}
          sx={{ ml: 1 }}
        >
          {loading ? 'Syncing' : 'Sync'}
        </Button>
        <SyncDetails
          data={res?.syncedScore || values}
          disabled={loading || !Object.keys(res?.syncedScore || values).length}
        />
      </Stack>
      <FormControlLabel
        control={<Checkbox {...register('otherFedItcBonus')} />}
        label={
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 'bold',
                color: theme.palette.textColor.main,
                mr: 1,
                mt: 0.25,
              }}
            >
              Other Federal ITC bonus?
            </Typography>
            <Tooltip
              placement="bottom"
              enterDelay={250}
              leaveDelay={400}
              title='In rare cases, the ITC used in View Analyze will be something other than 30% or 40% (when incentives other than the Energy Community are used). In this case, check the "Other Federal ITC Bonus" box, and the calculation for this item will be modified.'
            >
              <InfoOutlined
                sx={{
                  fontSize: 12,
                  color: theme.palette.primary.light,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </Stack>
        }
      />
    </Box>
  )
}

function ScoreForm({
  values = {},
  onScoreSubmit,
  loading,
}: {
  values?: any
  onScoreSubmit: (scores: any) => void
  loading: boolean
}) {
  const { site } = useContext(ReadinessContext) as any
  const theme = useTheme()
  const { analyze, ...restDefaultValues } = values

  console.log(analyze)

  const defaultValues = site.project ? values : restDefaultValues

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(scoreSchema(site)),
  })

  const [pickerOpen, setPickerOpen] = useState<string | null>(null)
  const [localLinkedFiles, setLocalLinkedFiles] = useState<
    Record<string, any[]>
  >(() => {
    const initial: Record<string, any[]> = {}
    const subscoreKeys = [
      'analyze',
      'interconnect',
      'landowner',
      'permitting',
      'offtake',
      'taxCredits',
      'buildability',
    ]

    subscoreKeys.forEach(key => {
      if (values[key]?.linkedFiles) {
        initial[key] = values[key].linkedFiles
      }
    })

    return initial
  })

  const handleLinkFiles = (subscore: string, files: any[]) => {
    setLocalLinkedFiles(prev => ({
      ...prev,
      [subscore]: [...(prev[subscore] || []), ...files],
    }))
  }

  const handleUnlinkFile = (subscore: string, fileId: string) => {
    setLocalLinkedFiles(prev => ({
      ...prev,
      [subscore]: (prev[subscore] || []).filter(f => f.file !== fileId),
    }))
  }

  const handleAnalyzeScoreSync = (syncedScore: any) => {
    setValue('analyze.value', syncedScore.score, { shouldValidate: true })
    setValue('analyze.meta', syncedScore)
  }

  const handleSubmitWithFiles = (scores: any) => {
    const withFiles = { ...scores }

    Object.keys(localLinkedFiles).forEach(key => {
      if (withFiles[key]) {
        withFiles[key] = {
          ...withFiles[key],
          linkedFiles: localLinkedFiles[key].map(file => ({
            dataroom: file.dataroom,
            dataroomName: file.dataroomName,
            stage: file.stage,
            stageName: file.stageName,
            file: file.file,
            filename: file.filename,
            url: file.url,
            mimetype: file.mimetype,
          })),
        }
      }
    })

    onScoreSubmit(withFiles)
  }

  const renderSubscoreBlock = (subscoreKey: string, label: string) => (
    <Grid size={6} key={subscoreKey}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          bgcolor: theme.palette.neutral.light + '20',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 'bold',
            color: theme.palette.textColor.main,
          }}
        >
          {label}
        </Typography>
        <Divider />
        <Typography sx={{ fontSize: 12, color: theme.palette.textColor.main }}>
          Score*
        </Typography>
        <TextField
          {...register(`${subscoreKey}.value`)}
          type="number"
          size="small"
          fullWidth
          sx={{ '& input': { fontSize: 12 }, bgcolor: 'white' }}
          error={!!(errors as any)[subscoreKey]?.value}
          helperText={(errors as any)[subscoreKey]?.value?.message}
        />
        <Typography sx={{ fontSize: 12, color: theme.palette.textColor.main }}>
          Description
        </Typography>
        <TextField
          {...register(`${subscoreKey}.description`)}
          multiline
          rows={2}
          size="small"
          fullWidth
          sx={{ '& textarea': { fontSize: 12 }, bgcolor: 'white' }}
          error={!!(errors as any)[subscoreKey]?.description}
          helperText={(errors as any)[subscoreKey]?.description?.message}
        />
        {site.id && (
          <>
            <LinkedFilesList
              files={localLinkedFiles[subscoreKey]}
              onUnlink={fileId => handleUnlinkFile(subscoreKey, fileId)}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1, fontSize: 11 }}
              onClick={() => setPickerOpen(subscoreKey)}
            >
              Link Files
            </Button>
          </>
        )}
      </Paper>
    </Grid>
  )

  return (
    <Stack
      component="form"
      spacing={1}
      noValidate
      onSubmit={handleSubmit(handleSubmitWithFiles)}
    >
      {site.project && (
        <Grid container spacing={1}>
          <Grid size={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                height: '100%',
                bgcolor: theme.palette.neutral.light + '20',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: theme.palette.textColor.main,
                }}
              >
                View Analyze
              </Typography>
              <Divider />
              <Typography
                sx={{ fontSize: 12, color: theme.palette.textColor.main }}
              >
                Score*
              </Typography>
              <TextField
                {...register('analyze.value')}
                type="number"
                size="small"
                fullWidth
                sx={{ '& input': { fontSize: 12 }, bgcolor: 'white' }}
                error={!!(errors as any)['analyze']?.value}
                helperText={(errors as any)['analyze']?.value?.message}
              />
              <Typography
                sx={{ fontSize: 12, color: theme.palette.textColor.main }}
              >
                Description
              </Typography>
              <TextField
                {...register('analyze.description')}
                multiline
                rows={2}
                size="small"
                fullWidth
                sx={{ '& textarea': { fontSize: 12 }, bgcolor: 'white' }}
                error={!!(errors as any)['analyze']?.description}
                helperText={(errors as any)['analyze']?.description?.message}
              />
              {site.id && (
                <>
                  <LinkedFilesList
                    files={localLinkedFiles.analyze}
                    onUnlink={fileId => handleUnlinkFile('analyze', fileId)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1, fontSize: 11 }}
                    onClick={() => setPickerOpen('analyze')}
                  >
                    Link Files
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
          <Grid size={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                height: '100%',
                bgcolor: theme.palette.neutral.light + '20',
              }}
            >
              <SyncAnalyzeScoreForm
                onSync={handleAnalyzeScoreSync}
                values={values.analyze?.meta || {}}
              />
            </Paper>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={1}>
        {SUBSCORES.map(({ key, label }) => renderSubscoreBlock(key, label))}
      </Grid>

      <Grid container spacing={1}>
        <Grid size={6}>
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: theme.palette.neutral.light + '20' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  {...register('published')}
                  defaultChecked={!!values.published}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: theme.palette.textColor.main,
                    mt: 0.25,
                  }}
                >
                  Publish?
                </Typography>
              }
            />
          </Paper>
        </Grid>
      </Grid>

      <Stack direction="row" sx={{ justifyContent: 'flex-end', p: 1 }}>
        <Button
          variant="contained"
          type="submit"
          size="small"
          disabled={loading}
        >
          Save Changes
        </Button>
      </Stack>

      {pickerOpen && (
        <FilePickerDialog
          open={!!pickerOpen}
          siteId={site.id}
          alreadyLinked={localLinkedFiles[pickerOpen] || []}
          onClose={() => setPickerOpen(null)}
          onConfirm={(files: any[]) => {
            handleLinkFiles(pickerOpen, files)
            setPickerOpen(null)
          }}
        />
      )}
    </Stack>
  )
}

const cleanTypename = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(cleanTypename)
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([k]) => k !== '__typename')
        .map(([k, v]) => [k, cleanTypename(v)])
    )
  }
  return obj
}

function UpdateScore({
  score,
  onCompleted,
}: {
  score: any
  onCompleted: () => void
}) {
  const { site } = useContext(ReadinessContext) as any
  const [updateScore, { loading }] = useMutation(UPDATE_SCORE)
  const client = useApolloClient()

  const handleScoreSubmit = (scores: any) => {
    const { published, ...restScores } = scores
    const readinessScore = {
      site: site.id,
      subscores: cleanTypename(restScores),
      published,
    }

    updateScore({
      variables: { score: readinessScore, id: score.id },
      onCompleted: (data: any) => {
        client.writeQuery({
          query: FIND_SCORES,
          variables: { filter: { site: site.id } },
          data: {
            scores: [
              {
                ...data.updateScore,
              },
            ],
          },
        })
        onCompleted()
      },
    })
  }

  return (
    <ScoreForm
      onScoreSubmit={handleScoreSubmit}
      loading={loading}
      values={{ ...score.subscores, published: score.published }}
    />
  )
}

function CreateScore({ onCompleted }: { onCompleted: () => void }) {
  const { site } = useContext(ReadinessContext) as any
  const [createScore, { loading }] = useMutation(CREATE_SCORE)
  const client = useApolloClient()

  const handleScoreSubmit = (scores: any) => {
    const { published, ...restScores } = scores
    const readinessScore = {
      site: site.id,
      subscores: cleanTypename(restScores),
      published,
    }

    createScore({
      variables: { score: readinessScore },
      onCompleted: (data: any) => {
        client.writeQuery({
          query: FIND_SCORES,
          variables: { filter: { site: site.id } },
          data: {
            scores: [
              {
                ...data.createScore,
              },
            ],
          },
        })
        onCompleted()
      },
    })
  }

  return <ScoreForm onScoreSubmit={handleScoreSubmit} loading={loading} />
}

function ReadinessScore({ onCompleted }: { onCompleted: () => void }) {
  const { site } = useContext(ReadinessContext) as any
  const theme = useTheme()
  const { loading, error, data } = useQuery(FIND_SCORES, {
    variables: { filter: { site: site.id } },
  })

  if (loading) return <CircularProgress size={24} />
  if (error) return <Typography color="error">error...</Typography>

  const { scores } = data

  return (
    <>
      {scores.length > 0 ? (
        <>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 'bold',
              color: theme.palette.textColor.main,
              ml: 1,
            }}
          >
            Score - {numeral(scores[0].value).format('0.[0]')}
          </Typography>
          <UpdateScore score={scores[0]} onCompleted={onCompleted} />
        </>
      ) : (
        <CreateScore onCompleted={onCompleted} />
      )}
    </>
  )
}

function Score({ site }: { site: any }) {
  const [show, setShow] = useState(false)
  const theme = useTheme()

  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ ml: 1 }}
        onClick={() => setShow(true)}
      >
        Readiness
      </Button>
      <Dialog
        open={show}
        onClose={() => setShow(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.textColor.main,
                }}
              >
                Readiness Score | {site.name}
              </Typography>
              <Tooltip
                title='Please enter a numeric value from 0 to 10 for each "Score" on this form (except the automated View Analyze subscore). Each "Description" should include supporting text, to be seen by our customers, describing the facts that lead to each subscore. If you don&apos;t know enough to judge a given subscore, enter "5". This is the value for "no-knowledge," and also for subscores that are "at the median". You can uncheck the "Publish" box and save your changes if you are awaiting some data. In that case, it may be helpful to leave yourself an ALL CAPS note in the appropriate "description" boxes, reminding you of which ones need attention.'
                placement="bottom"
                enterDelay={250}
                leaveDelay={400}
              >
                <InfoOutlined
                  sx={{
                    fontSize: 16,
                    color: theme.palette.primary.light,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {site.project && (
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={`${REACT_APP_USER_CLIENT}/analyze/projects/${site.project}/results`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Project
                </Button>
              )}
              <IconButton size="small" onClick={() => setShow(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ReadinessContext.Provider value={{ site } as any}>
            <ReadinessScore onCompleted={() => setShow(false)} />
          </ReadinessContext.Provider>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Score
